import {
  forwardRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from "react";
import { Copy } from "./Icon";

/**
 * Token-only controls. Compose the roles of `@norkron/web-controls` (Button),
 * `@norkron/web-forms` (Field), `@norkron/web-cards` (Card). No raw hex — every
 * surface reads `--norkron-*` / `--ela-*`. SWAP-IN: replace with the published
 * packages once the workspace resolves; the props here are intentionally close.
 */

type Variant = "primary" | "secondary" | "ghost";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  block?: boolean;
  icon?: ReactNode;
}

export function Button({
  variant = "secondary",
  block,
  icon,
  children,
  className,
  ...rest
}: ButtonProps) {
  const cls = [
    "btn",
    `btn--${variant}`,
    block ? "btn--block" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <button className={cls} {...rest}>
      {icon}
      {children}
    </button>
  );
}

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  hint?: string;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  function TextField({ label, error, hint, id, ...rest }, ref) {
    const fieldId = id ?? `f-${label.replace(/\s+/g, "-").toLowerCase()}`;
    return (
      <div className="field">
        <label className="field__label" htmlFor={fieldId}>
          {label}
        </label>
        <input
          ref={ref}
          id={fieldId}
          className="field__input"
          aria-invalid={error ? true : undefined}
          {...rest}
        />
        {hint && !error ? <span className="field__hint">{hint}</span> : null}
        {error ? (
          <span className="field__error" role="alert">
            {error}
          </span>
        ) : null}
      </div>
    );
  },
);

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  options: { value: string; label: string }[];
}

export function SelectField({ label, options, id, ...rest }: SelectFieldProps) {
  const fieldId = id ?? `s-${label.replace(/\s+/g, "-").toLowerCase()}`;
  return (
    <div className="field">
      <label className="field__label" htmlFor={fieldId}>
        {label}
      </label>
      <select id={fieldId} className="field__select" {...rest}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export function Card({
  children,
  className,
  ...rest
}: { children: ReactNode; className?: string } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={["card", className ?? ""].join(" ")} {...rest}>
      {children}
    </div>
  );
}

/**
 * A real two-state toggle switch (NOT a dropdown) — the primitive behind the
 * permission MATRIX cells and the Users-tab internal/external + status toggles.
 * Renders an accessible `role="switch"` button (`aria-checked`), so screen
 * readers + Playwright/testing-library see the on/off state. `data-testid` and
 * any other button props pass through.
 */
export function Switch({
  checked,
  onChange,
  disabled,
  label,
  className,
  ...rest
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  label?: string;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onChange">) {
  const cls = [
    "switch",
    checked ? "switch--on" : "switch--off",
    disabled ? "switch--disabled" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className={cls}
      onClick={() => {
        if (!disabled) onChange(!checked);
      }}
      {...rest}
    >
      <span className="switch__track" aria-hidden="true">
        <span className="switch__thumb" />
      </span>
    </button>
  );
}

/** Copyable mono content-hash chip (elaborate-owned HashChip, component-map §4). */
export function HashChip({
  hash,
  short = true,
  onCopy,
}: {
  hash: string;
  short?: boolean;
  onCopy?: (hash: string) => void;
}) {
  const label = short ? `${hash.slice(0, 8)}…${hash.slice(-4)}` : hash;
  return (
    <button
      type="button"
      className="hashchip"
      data-testid="hash-chip"
      title={hash}
      onClick={() => {
        void navigator.clipboard?.writeText(hash).catch(() => {});
        onCopy?.(hash);
      }}
    >
      <span>{label}</span>
      <Copy size={13} strokeWidth={1.5} />
    </button>
  );
}
